<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="850px"
    :scrollable="true"
    card-class="blue-grey lighten-5"
    small-title
  >
    <template slot="dialogTitle">
      <span>
        Set production and delivery plan
      </span>
    </template>

    <v-card-text class="pa-8">
      <validation-observer ref="validationObserver">
        <template v-for="(deliveryStep, i) in deliveryPlan">
          <v-card
            :key="'step-'+i"
            color="white"
            elevation="10"
            class="pa-4"
          >
            <v-row
              no-gutters
              align="center"
            >
              <v-col
                cols="auto"
                class="px-4"
              >
                <v-avatar
                  size="26px"
                  color="primary"
                  class="white--text caption"
                >
                  {{ i + 1 }}
                </v-avatar>
              </v-col>
              <v-col>
                <div class="subtitle-2 grey--text text--darken-2">
                  {{ deliveryStep.title }}
                </div>
                <div
                  v-if="deliveryStep.processor && deliveryStep.processor.country"
                  class="caption grey--text"
                >
                  <flag
                    class="mr-1"
                    :iso="deliveryStep.processor.country.code"
                    :squared="false"
                  />
                  {{ deliveryStep.processor.country.name }}
                  &middot;
                  {{ deliveryStep.processor.name }}
                </div>
                <div
                  v-else-if="['base_delivery', 'charter_start', 'charter_end'].some(t => t === deliveryStep.type)"
                  class="caption grey--text"
                >
                  <flag
                    class="mr-1"
                    :iso="yacht.country.code"
                    :squared="false"
                  />
                  {{ yacht.country.name }}
                  <span v-if="deliveryBase">
                    &middot;
                    {{ deliveryBase.name }}
                  </span>
                </div>
              </v-col>
              <v-col
                cols="5"
                @click="showDatePickerFor(i, deliveryStep.date)"
              >
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                  name="date"
                >
                  <v-text-field
                    :ref="'date_' + i"
                    :value="deliveryStep.date ? $moment(deliveryStep.date).format('DD.MM.YYYY.') : ''"
                    :error-messages="errors"
                    outlined
                    hide-details
                    label="Date"
                    append-icon="event"
                    readonly
                  />
                </validation-provider>
              </v-col>
            </v-row>
            <!--<editor-fold desc="Truckings">-->
            <div v-if="deliveryStep.type === 'trucking'">
              <v-divider class="grey--text my-4" />
              <v-row
                no-gutters
                align="center"
              >
                <v-col cols="1" />
                <v-col class="pl-3 grey--text text--darken-1">
                  Select trucking company
                </v-col>
                <v-col cols="5">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="required"
                    name="date"
                  >
                    <v-select
                      v-model="deliveryStep.processor"
                      :name="'trucking_company-'+i"
                      outlined
                      hide-details
                      label="Trucking Company"
                      :items="truckingCompanies.data"
                      :loading="truckingCompanies.loading"
                      item-text="name"
                      item-value="id"
                      return-object
                      :error-messages="errors"
                    />
                  </validation-provider>
                </v-col>
              </v-row>
            </div>
            <!--</editor-fold>-->
            <!--<editor-fold desc="Delivery Base">-->
            <div v-if="deliveryStep.type === 'base_delivery'">
              <v-divider class="grey--text my-4" />
              <v-row
                no-gutters
                align="center"
              >
                <v-col cols="1" />
                <v-col class="pl-3 grey--text text--darken-1">
                  Select final destination base
                </v-col>
                <v-col cols="5">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="required"
                    name="date"
                  >
                    <v-select
                      v-model="deliveryBase"
                      :error-messages="errors"
                      outlined
                      hide-details
                      label="Destination Base"
                      :items="bases"
                      item-text="name"
                      item-value="id"
                      return-object
                    />
                  </validation-provider>
                </v-col>
              </v-row>
            </div>
            <!--</editor-fold>-->
          </v-card>
          <v-row
            :key="'range-'+i"
            no-gutters
          >
            <v-col
              v-if="deliveryPlan[i + 1]"
              cols="2"
              offset="12"
              class="grey--text text--darken-1 text-center py-1"
              style="font-size: 12px; line-height: 11px !important;"
            >
              <v-icon
                style="font-size: 10px; vertical-align: baseline"
                class="mr-1"
              >
                fa fa-arrow-down
              </v-icon>
              {{ (deliveryPlan[i + 1] && deliveryPlan[i + 1].date && deliveryStep.date) ? $moment(deliveryPlan[i + 1].date).diff($moment(deliveryStep.date), 'days') : '-' }} days
            </v-col>
          </v-row>
        </template>
      </validation-observer>

      <v-menu
        v-model="datePicker.show"
        :close-on-content-click="false"
        transition="fade-transition"
        min-width="290px"
        absolute
        nudge-bottom="60px"
        :position-x="datePicker.position.x"
        :position-y="datePicker.position.y"
      >
        <v-date-picker
          ref="datePicker"
          v-model="datePicker.date"
          no-title
          :first-day-of-week="1"
          :min="datePicker.minDate"
          @input="setDatePickerDate"
        />
      </v-menu>
    </v-card-text>

    <template slot="dialogActions">
      <v-btn
        v-if="yacht.delivery_plan.saved"
        text
        color="red"
        small
        :loading="deleting"
        @click="deletePlan"
      >
        <v-icon
          left
          x-small
        >
          fa fa-trash
        </v-icon>
        Delete plan
      </v-btn>
      <v-spacer />
      <v-btn
        outlined
        color="grey darken-2"
        :disabled="!isChanged"
        @click="revert()"
      >
        <v-icon
          small
          left
        >
          undo
        </v-icon>
        Undo changes
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
  </content-dialog>
</template>

<script>
import ContentDialog from '@/components/helpers/Dialog'
import cloneDeep from 'lodash/cloneDeep'
import Yacht from '@/models/Yacht'
import Contractor from '@/models/Contractor'
import { CHARTER_DEFAULT_DELAY, PRODUCTION_DEFAULT_DURATION } from '@/models/DeliveryStep'
import validationScrollMixin from '@/mixins/validationScrollMixin'

export default {
    name: 'DeliveryPlanDialog',
    components: {
      ContentDialog
    },
    mixins: [
      validationScrollMixin
    ],
    props: {
      visible: Boolean,
      yacht: {
        type: Object,
        default: null
      },
      step: {
        type: Number,
        default: null
      }
    },
    data() {
      return {
        deliveryPlan: [],
        originalDeliveryPlan: [],
        submitting: false,
        currentStep: null,
        datePicker: {
          show: false,
          date: null,
          startOrEnd: null,
          minDate: null,
          position: {
            x: 0,
            y: 0
          }
        },
        deliveryBase: null,
        truckingCompanies: {
          data: [],
          loading: false
        },
        deleting: false,
        isChanged: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      bases: function () {
        return this.$store.state.business.bases.data.filter(b => b.country_id === this.yacht.country_id)
      }
    },
    watch: {
      yacht: function () {
        this.loadDeliveryPlan()
      },
      step: function (step) {
        if (!this.deliveryPlan || !step) return

        this.currentStep = this.deliveryPlan.findIndex(s => s.id === step) + 1
      },
      'datePicker.show': function (value) {
        if (value) return

        this.datePicker.date = null
        this.datePicker.startOrEnd = null
        this.datePicker.minDate = null
      }
    },
    created() {
      this.PRODUCTION_DEFAULT_DURATION = PRODUCTION_DEFAULT_DURATION
      this.CHARTER_DEFAULT_DELAY = CHARTER_DEFAULT_DELAY
      this.fetchTruckingCompanies()
      this.loadDeliveryPlan()
    },
    methods: {
      loadDeliveryPlan: function () {
        if (!this.yacht || !this.yacht.delivery_plan) return

        this.deliveryPlan = cloneDeep(this.yacht.delivery_plan.steps)

        for (let step of this.deliveryPlan) {
          step.date = step.estimated_at || null
        }

        this.originalDeliveryPlan = cloneDeep(this.deliveryPlan)

        this.deliveryBase = this.yacht.destination_base || this.yacht.current_base
      },
      showDatePickerFor: function (index, currentDate) {
        this.currentStep = index
        if (currentDate) this.datePicker.date = this.$moment(currentDate).format('YYYY-MM-DD')

        this.setDatePickerMinDate()
        this.setDatePickerPosition()

        this.$nextTick(() => {
          this.datePicker.show = true
        })
      },
      setDatePickerDate: function (value) {
        this.updateStepDate(this.currentStep, value)

        this.datePicker.show = false

        this.isChanged = true
      },
      updateStepDate: function (step, date) {
        let diffInDays = this.$moment(date).diff(this.$moment(this.deliveryPlan[step]['date']), 'days')

        this.deliveryPlan[step]['date'] = date

        this.updateNextSteps(step + 1, diffInDays || 0)
      },
      updateNextSteps: function (startStep = 1, addDays = 0) {
        if (!this.deliveryPlan[startStep - 1] || !this.deliveryPlan[startStep - 1]['date'] || !this.deliveryPlan[startStep]) return

        for (let step = startStep; step < this.deliveryPlan.length; step++) {
          if (this.deliveryPlan[step]['date']) {
            // if startDate exist, update
            this.deliveryPlan[step]['date'] = this.$moment(this.deliveryPlan[step]['date']).add(addDays, 'days')
          }

          if (this.deliveryPlan[step]['type'] === 'charter_end') {
            this.populateCharterEndStep(step)
            return
          }
        }
      },
      setDatePickerMinDate: function () {
        if (this.deliveryPlan[this.currentStep - 1]) {
          this.datePicker.minDate = this.deliveryPlan[this.currentStep - 1].date
        }
      },
      setDatePickerPosition: function () {
        let rect = this.$refs[`date_${this.currentStep}`][0].$el.getBoundingClientRect()
        this.datePicker.position.x = rect.left
        this.datePicker.position.y = rect.top
      },
      populateCharterEndStep: function (step) {
        let charterStartStep = this.deliveryPlan.find(p => p.type === 'charter_start')

        if (!this.yacht.country || !this.yacht.country.charter_end_date || !this.yacht.finance_model_params || !this.yacht.finance_model_params.investmentPeriodMonths || !charterStartStep || !charterStartStep['date']) return

        let endDate = this.yacht.country.charter_end_date.split('-')
        let endDay = endDate[1]
        let endMonth = endDate[0]

        let investmentPeriodMonths = this.yacht.finance_model_params.investmentPeriodMonths

        this.deliveryPlan[step]['date'] = this.$moment(charterStartStep['date'], 'YYYY-MM-DD')
          .add(investmentPeriodMonths - 12, 'months')
          .month(endMonth - 1)
          .date(endDay)
          .format('YYYY-MM-DD')
      },
      getStepsPostData: function () {
        return this.deliveryPlan.map(step => {
          return {
            id: step.id,
            type: step.type,
            date: this.$moment(step.date).format('YYYY-MM-DD'),
            processor_id: step.processor ? step.processor.id : null
          }
        })
      },
      submit: async function () {
        let validation = await this.$refs.validationObserver.validate()

        if (!validation) {
          this.showAlertAndScrollToFirstError()
          return
        }

        this.storeDeliveryPlan()
      },
      storeDeliveryPlan: async function () {
        this.submitting = true

        try {
          let response = await Yacht.api.storeDeliveryPlan(this.yacht.id, this.getStepsPostData(), this.deliveryBase.id)

          this.modalVisible = false
          this.$emit('update:yacht', response.yacht)
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong',
            color: 'error',
            multiline: true
          })
          throw e
        } finally {
          this.submitting = false
        }
      },
      fetchTruckingCompanies: async function () {
        this.truckingCompanies.loading = true

        try {
          this.truckingCompanies.data = await Contractor.api.fetch({ type: Contractor.TYPE_TRUCKING, all: true })
        } catch (e) {
          throw e
        } finally {
          this.truckingCompanies.loading = false
        }
      },
      deletePlan: async function () {
        try {
          this.deleting = true

          let response = await Yacht.api.deleteDeliveryPlan(this.yacht.id)

          this.$emit('update:yacht', response.yacht)

          this.modalVisible = false
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong',
            color: 'error',
            multiline: true
          })
          throw e
        } finally {
          this.deleting = false
        }
      },
      revert: function () {
        this.deliveryPlan = cloneDeep(this.originalDeliveryPlan)

        this.isChanged = false
      }
    }
  }
</script>

<style lang="scss">
  @import "~vuetify/src/components/VStepper/VStepper";

  .v-stepper__step--vertical .v-stepper__label {
    width: 100%;
    flex-direction: row;
  }
</style>
